/**
 * RegisterBook.jsx: BM-H3613 (5 Oct 2026). The firm's registers, held in CaseMinder.
 *
 * Vanessa: "registers need to live here". SharePoint is now an archive. Each
 * register has its own columns (the firm's workbook headings), a live and a
 * closed list, an add form and an export. Who sees what is decided by the
 * server: the register's owner, the MLCO, the COLP and the directors see every
 * entry; others see their own. The Suspicious Activity Report Register is the
 * MLRO's and the MLCO's only.
 * BM-H4060 (8 Oct 2026): REG-21, the Audit Corrective Actions Register, shows
 * the person each action names, marks an action past its due date overdue,
 * and lists the corrective actions recorded on file reviews alongside.
 * Server: /api/compliance/register-book.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { ArrowLeft, ChevronRight, ClipboardCheck, Download, FileSearch, Plus } from "lucide-react";
import PageHero from "../../components/PageHero";
import { registerLook } from "./registerLook";

function authHeaders() {
  const id = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return id ? { "X-Bonsai-User-Id": id } : {};
}

const INPUT = "w-full px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-100";
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "");

function Field({ fd, value, onChange, idPrefix }) {
  const id = `${idPrefix}-${fd.key}`;
  const label = (
    <label htmlFor={id} className="block text-xs text-slate-300 mb-0.5">
      {fd.label}{fd.required ? " *" : ""}
    </label>
  );
  let input;
  if (fd.type === "long") {
    input = <textarea id={id} rows={3} className={INPUT} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  } else if (fd.type === "yesno" || fd.type === "choice") {
    input = (
      <select id={id} className={INPUT} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {(fd.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else {
    const type = fd.type === "date" ? "date" : (fd.type === "money" || fd.type === "number") ? "number" : "text";
    input = <input id={id} type={type} className={INPUT} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className={fd.type === "long" ? "sm:col-span-2" : ""}>
      {label}
      {input}
      {fd.help && <div className="text-[11px] text-slate-400 mt-0.5">{fd.help}</div>}
    </div>
  );
}

function EntryForm({ fields, initial, submitLabel, onSubmit, onCancel, idPrefix }) {
  const [values, setValues] = useState(initial || {});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(errorMessage(err, "This could not be saved"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 p-3 rounded-md border border-slate-700/60" data-testid={`${idPrefix}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((fd) => (
          <Field key={fd.key} fd={fd} value={values[fd.key]} idPrefix={idPrefix}
            onChange={(v) => setValues({ ...values, [fd.key]: v })} />
        ))}
      </div>
      {error && <div className="text-sm text-red-300" data-testid={`${idPrefix}-error`}>{error}</div>}
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={submit} data-testid={`${idPrefix}-submit`}
          className="px-3 py-1.5 text-sm rounded-md bg-emerald-700 text-white disabled:opacity-50">{submitLabel}</button>
        {onCancel && <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md border border-slate-600 text-slate-200">Cancel</button>}
      </div>
    </div>
  );
}

function RegisterList() {
  const [regs, setRegs] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${API}/compliance/register-book`, { headers: authHeaders() })
      .then((r) => setRegs(r.data.registers || []))
      .catch((err) => setError(errorMessage(err, "The registers could not be loaded")));
  }, []);
  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <PageHero icon={ClipboardCheck} title="Registers" testId="register-book-hero"
        subtitle="The firm's compliance registers. Record an entry in the register your policy names.">
        {regs && (
          <span className="cm-glass rounded-full px-3 py-1 text-xs text-indigo-100" data-testid="register-book-tally">
            {regs.reduce((n, r) => n + (r.live || 0), 0)} live entries · {regs.length} registers
          </span>
        )}
      </PageHero>
      {error && <div className="text-sm text-red-300">{error}</div>}
      {regs && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="register-book-list">
          {regs.map((r) => {
            const { Icon, style } = registerLook(r.ref);
            return (
              <Link key={r.ref} to={`/compliance/registers/${r.ref}`} data-testid={`register-book-${r.ref}`} style={style}
                className="cm-card group relative flex items-center gap-3 overflow-hidden rounded-xl border border-white/[0.07] bg-navy-card px-3 py-2.5">
                <div aria-hidden className="absolute inset-y-0 left-0 w-1" style={{ background: "linear-gradient(180deg, var(--c1), var(--c2))" }} />
                <span className="cm-tile flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="h-[18px] w-[18px] text-white" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-100 truncate">{r.name}</div>
                  <div className="text-[11px] text-slate-400">
                    <span className="font-mono">{r.ref} · {r.policy}</span>
                    {" · "}
                    {r.sees_all ? `${r.live} live, ${r.closed} closed` : `${r.live + r.closed} of your entries`}
                    {r.proposed_columns ? " · columns proposed, for review" : ""}
                  </div>
                </div>
                {r.sees_all && r.live > 0 && (
                  <span className="cm-glass rounded-full px-2 py-0.5 text-xs font-semibold text-white" aria-hidden="true">{r.live}</span>
                )}
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RegisterView({ refCode }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("live");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [closing, setClosing] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/compliance/register-book/${refCode}`, { headers: authHeaders() })
      .then((r) => { setData(r.data); setError(""); })
      .catch((err) => setError(errorMessage(err, "The register could not be loaded")));
  }, [refCode]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-6 text-sm text-red-300" data-testid="register-book-error">{error}</div>;
  if (!data) return <div className="p-6 text-sm text-slate-400">Loading…</div>;

  const shown = data.entries.filter((e) => e.status === tab);
  const look = registerLook(data.ref);
  const count = (st) => data.entries.filter((e) => e.status === st).length;
  const cell = (c, v) => (c.type === "date" && v ? day(v) : String(v ?? ""));
  const tabCls = (on) => `inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded-full border transition ${on
    ? "border-indigo-400/60 bg-indigo-400/15 text-white" : "border-white/10 text-slate-300 hover:text-white"}`;
  const keyCols = data.list_columns?.length
    ? data.list_columns.map((k) => data.fields.find((f) => f.key === k)).filter(Boolean)
    : data.fields.slice(0, 4);
  const linked = (data.linked || []).filter((a) => a.status === tab);
  const add = async (values) => {
    await axios.post(`${API}/compliance/register-book/${refCode}/entries`, { fields: values }, { headers: authHeaders() });
    setAdding(false);
    load();
  };
  const update = async (entry, values) => {
    await axios.post(`${API}/compliance/register-book/${refCode}/entries/${entry.id}/update`, { fields: values }, { headers: authHeaders() });
    setEditing(null);
    load();
  };
  const close = async (entry, values) => {
    await axios.post(`${API}/compliance/register-book/${refCode}/entries/${entry.id}/close`, { closing: values }, { headers: authHeaders() });
    setClosing(null);
    load();
  };
  const exportCsv = async () => {
    const r = await axios.get(`${API}/compliance/register-book/${refCode}/export`, { headers: authHeaders(), responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${refCode} ${data.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl" data-testid="register-book-view">
      <Link to="/compliance/registers" className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All registers
      </Link>
      <div>
        <PageHero icon={look.Icon} title={data.name} tile={`linear-gradient(135deg, ${look.c1}, ${look.c2})`}
          subtitle={<span className="font-mono">{data.ref} · {data.policy} · owner {data.owner}</span>} />
        {!data.sees_all && (
          <div className="text-xs text-slate-400">
            {data.owner_field
              ? "You see the actions that name you. The register's owner sees them all."
              : "You see the entries you made. The register's owner sees them all."}
          </div>
        )}
        {data.proposed_columns && (
          <div className="text-xs text-amber-300" data-testid="register-book-proposed">
            No policy lists this register's columns. These are proposed, for the owner to confirm.
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" onClick={() => setTab("live")} data-testid="register-book-tab-live"
          className={tabCls(tab === "live")}>Live <span className="text-xs text-slate-400">{count("live")}</span></button>
        {data.closed_label && (
          <button type="button" onClick={() => setTab("closed")} data-testid="register-book-tab-closed"
            className={tabCls(tab === "closed")}>{data.closed_label} <span className="text-xs text-slate-400">{count("closed")}</span></button>
        )}
        <div className="flex-1" />
        {data.can_add && !adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="register-book-add"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-white shadow-lg shadow-indigo-950/40"
            style={{ background: `linear-gradient(135deg, ${look.c1}, ${look.c2})` }}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Add an entry
          </button>
        )}
        {data.sees_all && (
          <button type="button" onClick={exportCsv} data-testid="register-book-export" title="Export to CSV"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-white/10 text-slate-200 hover:text-white">
            <Download className="h-4 w-4" aria-hidden="true" /> Export
          </button>
        )}
      </div>
      {adding && <EntryForm fields={data.fields} submitLabel="Add to the register" idPrefix="add" onSubmit={add} onCancel={() => setAdding(false)} />}
      {shown.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="register-book-empty">No {tab === "live" ? "live" : (data.closed_label || "closed").toLowerCase()} entries.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/[0.07] bg-navy-card" style={look.style}>
          <div aria-hidden className="h-0.5" style={{ background: "linear-gradient(90deg, var(--c1), var(--c2))" }} />
          <table className="w-full text-sm text-slate-200" data-testid="register-book-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="py-2 pl-3 pr-3 font-medium">Reference</th>
                {keyCols.map((c) => <th key={c.key} className="py-2 pr-3 font-medium">{c.label}</th>)}
                <th className="py-2 pr-3 font-medium">Added</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.id} className={`border-t border-white/[0.06] align-top hover:bg-white/[0.03] ${e.overdue ? "shadow-[inset_3px_0_0_#ef4444]" : ""}`} data-testid="register-book-row">
                  <td className="py-2 pl-3 pr-3 font-mono text-xs whitespace-nowrap">
                    {e.reference}
                    {e.overdue && <div><span className="px-1.5 py-0.5 rounded bg-red-900/50 text-red-200 text-[10px] font-sans" data-testid="register-book-overdue">Overdue</span></div>}
                  </td>
                  {keyCols.map((c) => <td key={c.key} className={`py-2 pr-3 ${c.type === "date" ? "whitespace-nowrap" : ""}`}>{cell(c, e.fields?.[c.key])}</td>)}
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">{day(e.created_at)}<div className="text-slate-400">{e.created_by}</div></td>
                  <td className="py-2 pr-3 text-right space-x-1 whitespace-nowrap">
                    {e.status === "live" && (
                      <button type="button" onClick={() => setEditing(e)} className="px-2 py-0.5 text-xs rounded border border-white/10 hover:border-indigo-400/60 hover:text-white">Open</button>
                    )}
                    {e.status === "live" && data.sees_all && data.closed_label && (
                      <button type="button" onClick={() => setClosing(e)} data-testid="register-book-close"
                        className="px-2 py-0.5 text-xs rounded border border-white/10 hover:border-indigo-400/60 hover:text-white">{data.closed_label === "Discharged" ? "Discharge" : "Close"}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.linked && (
        <div className="space-y-2" data-testid="register-book-linked">
          <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2"><FileSearch className="h-4 w-4 text-indigo-300" aria-hidden="true" /> From file reviews</h2>
          <div className="text-xs text-slate-400">Corrective actions recorded on a file review. Close them on the review itself.</div>
          {linked.length === 0 ? (
            <div className="text-sm text-slate-400" data-testid="register-book-linked-empty">No {tab === "live" ? "open" : "completed"} file review actions.</div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/[0.07] bg-navy-card px-3">
              <table className="w-full text-sm text-slate-200">
                <thead>
                  <tr className="text-left text-xs text-slate-400">
                    <th className="py-1 pr-3">Matter</th>
                    <th className="py-1 pr-3">Corrective action</th>
                    <th className="py-1 pr-3">Who must do it</th>
                    <th className="py-1 pr-3">Due by</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {linked.map((a) => (
                    <tr key={a.id} className="border-t border-slate-800/60 align-top" data-testid="register-book-linked-row">
                      <td className="py-1 pr-3 font-mono text-xs">
                        {a.matter_ref ? <Link to={`/matter/${encodeURIComponent(a.matter_ref)}`} className="text-indigo-300 hover:underline">{a.matter_ref}</Link> : null}
                      </td>
                      <td className="py-1 pr-3">{a.action}{a.closure_note ? <div className="text-xs text-slate-400">{a.closure_note}</div> : null}</td>
                      <td className="py-1 pr-3 text-xs">{a.owner}</td>
                      <td className="py-1 pr-3 text-xs">
                        {day(a.due_date)}
                        {a.overdue && <div><span className="px-1.5 py-0.5 rounded bg-red-900/50 text-red-200 text-[10px]" data-testid="register-book-overdue">Overdue</span></div>}
                      </td>
                      <td className="py-1 text-right whitespace-nowrap">
                        {a.review_link && <Link to={a.review_link} className="px-2 py-0.5 text-xs rounded border border-slate-600">Open review</Link>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {editing && (
        <div className="space-y-1">
          <div className="text-sm text-slate-200">{editing.reference}</div>
          <EntryForm fields={data.fields} initial={editing.fields} submitLabel="Save changes" idPrefix="edit"
            onSubmit={(v) => update(editing, v)} onCancel={() => setEditing(null)} />
        </div>
      )}
      {closing && (
        <div className="space-y-1">
          <div className="text-sm text-slate-200">{data.closed_label === "Discharged" ? "Discharge" : "Close"} {closing.reference}</div>
          <EntryForm fields={data.closing} submitLabel={data.closed_label === "Discharged" ? "Discharge" : "Close"} idPrefix="close"
            onSubmit={(v) => close(closing, v)} onCancel={() => setClosing(null)} />
        </div>
      )}
    </div>
  );
}

export default function RegisterBook() {
  const { ref } = useParams();
  return ref ? <RegisterView refCode={ref} /> : <RegisterList />;
}
